import { FC, useState } from 'react';
import { Form, Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from '@emotion/styled';
import { Text, Title } from 'components/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { authRoutes } from 'routes';
import {
  SendRemindPwdEmailDTO,
  sendRemindPwdEmailSchema,
} from 'pages/auth/authModels';
import { useSendResetPwdEmailMutation } from 'hooks/mutations/useSendResetPwdEmailMutation';
import { Input } from 'components/form/Input';
import { FormItem } from 'components/form/FormItem';
import { AuthCard } from 'pages/auth/components/AuthCard';

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  '& *': {
    textAlign: 'center',
  },
});

const Header = styled(Container)({
  marginBottom: '2rem',
});

const StyledFormItem = styled(FormItem)({
  marginBottom: '1.5rem',
});

const SubmitButton = styled(Button)({
  marginBottom: '0.5rem',
});

const ResendContainer = styled.div({
  marginTop: '2.25rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ResendText = styled(Text)`
  margin: 0;
`;

export const ResetPasswordPage: FC = () => {
  const [isSuccessCard, setIsSuccessCard] = useState(false);
  const methods = useForm<SendRemindPwdEmailDTO>({
    resolver: zodResolver(sendRemindPwdEmailSchema),
    mode: 'onBlur',
  });
  const { mutateAsync, isLoading } = useSendResetPwdEmailMutation();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const submit = (data: SendRemindPwdEmailDTO) => {
    mutateAsync(data).then(() => {
      setIsSuccessCard(true);
    });
  };

  return isSuccessCard ? (
    <AuthCard>
      <Container>
        <Title>
          <FormattedMessage id="auth.resetPassword.checkEmail" />
        </Title>
        <Text type="secondary">
          <FormattedMessage
            id="auth.resetPassword.linkSent"
            values={{ email: <b>{methods.getValues('email')}</b> }}
          />
        </Text>
        <ResendContainer>
          <ResendText type="secondary">
            <FormattedMessage id="auth.resetPassword.notReceived" />
          </ResendText>
          <Button
            type="link"
            loading={isLoading}
            onClick={() => submit(methods.getValues())}
          >
            <FormattedMessage id="auth.resetPassword.resend" />
          </Button>
        </ResendContainer>
      </Container>
    </AuthCard>
  ) : (
    <AuthCard>
      <Header>
        <Title>
          <FormattedMessage id="auth.resetPassword" />
        </Title>
        <Text type="secondary">
          <FormattedMessage id="auth.resetPassword.willSendLink" />
        </Text>
      </Header>
      <FormProvider {...methods}>
        <Form
          onFinish={methods.handleSubmit((data) => submit(data))}
          layout="vertical"
        >
          <StyledFormItem
            name="email"
            label={formatMessage({ id: 'field.email' })}
          >
            <Input name="email" autoComplete="email" />
          </StyledFormItem>
          <SubmitButton
            block
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={!methods.formState.isValid}
          >
            <FormattedMessage id="auth.resetPassword" />
          </SubmitButton>
          <Button
            block
            type="primary"
            loading={isLoading}
            onClick={() => navigate(authRoutes.login)}
          >
            <FormattedMessage id="cancel" />
          </Button>
        </Form>
      </FormProvider>
    </AuthCard>
  );
};
